import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  AMAZON_TAG_PATTERN,
  DEFAULT_AMAZON_DISCLOSURE,
  isValidAssociatesTag,
} from "@/lib/amazonAffiliate";
import type { Workspace } from "@/hooks/useWorkspace";

type Props = { workspace: Workspace };

type ProfileRow = {
  amazon_associates_tag: string | null;
  amazon_disclosure_text: string | null;
};

/**
 * Workspace-scoped Amazon Associates settings. Each workspace wires up its
 * own Associates tag — Excerptus never owns the affiliate relationship. When
 * a tag is set, references with an ISBN are rendered as affiliate links and
 * the disclosure line is auto-injected at the bottom of the document.
 *
 * Phase 2.5 scaffold: storage + URL building only. No PA-API calls.
 */
export const AmazonAffiliateSettingsCard = ({ workspace }: Props) => {
  const [tag, setTag] = useState("");
  const [disclosure, setDisclosure] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("amazon_associates_tag, amazon_disclosure_text")
        .eq("id", workspace.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        toast.error(error.message);
      }
      const row = (data ?? null) as ProfileRow | null;
      setTag(row?.amazon_associates_tag ?? "");
      setDisclosure(row?.amazon_disclosure_text ?? "");
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [workspace.id]);

  const trimmedTag = tag.trim();
  const tagInvalid = trimmedTag.length > 0 && !isValidAssociatesTag(trimmedTag);

  const handleSave = async () => {
    if (tagInvalid) {
      toast.error("Associates tag looks invalid (expected e.g. yourbrand-20).");
      return;
    }
    setSaving(true);
    const trimmedDisclosure = disclosure.trim();
    const { error } = await supabase
      .from("profiles")
      .update({
        amazon_associates_tag: trimmedTag.length > 0 ? trimmedTag : null,
        amazon_disclosure_text:
          trimmedDisclosure.length > 0 ? trimmedDisclosure : null,
      })
      .eq("id", workspace.id);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Amazon affiliate settings saved");
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle>Amazon affiliate links</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5 text-sm">
        <p className="text-muted-foreground">
          When a workspace Associates tag is set, references with an ISBN render
          as affiliate links in published drafts and the disclosure below is
          auto-appended. Leave blank to disable. Excerptus never collects from
          your affiliate links — every workspace uses its own tag.
        </p>

        <div className="space-y-2">
          <Label htmlFor={`amzn-tag-${workspace.id}`}>Associates tag</Label>
          {loading ? (
            <Skeleton className="h-10 w-full max-w-sm" />
          ) : (
            <Input
              id={`amzn-tag-${workspace.id}`}
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="yourbrand-20"
              className="max-w-sm"
              aria-invalid={tagInvalid}
            />
          )}
          <p className="text-xs text-muted-foreground">
            Format: 3–20 characters then <code>-20</code>, <code>-21</code>, or{" "}
            <code>-22</code> (matches <code>{AMAZON_TAG_PATTERN.source}</code>).
          </p>
          {tagInvalid && (
            <p className="text-xs text-destructive">
              Tag doesn't match Amazon's Associates format. Double-check it in
              your Associates dashboard.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor={`amzn-disclosure-${workspace.id}`}>
            Disclosure text
          </Label>
          {loading ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <Textarea
              id={`amzn-disclosure-${workspace.id}`}
              value={disclosure}
              onChange={(e) => setDisclosure(e.target.value)}
              placeholder={DEFAULT_AMAZON_DISCLOSURE}
              rows={3}
            />
          )}
          <p className="text-xs text-muted-foreground">
            Leave blank to use the default disclaimer. Appended once per
            published draft that contains affiliate links.
          </p>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={loading || saving || tagInvalid}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
